import { useEffect, useState } from "react";
import axios from "axios";
import { Warehouse, Plus, Trash, ArrowDown, ArrowUp, DownloadSimple, CaretLeft, Warning } from "@phosphor-icons/react";
import { useEscStep } from "@/lib/escBack";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const downloadFile = async (url, filename) => {
  try {
    const res = await axios.get(url, { responseType: "blob" });
    const objUrl = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement("a"); link.href = objUrl; link.setAttribute("download", filename);
    document.body.appendChild(link); link.click(); link.remove();
    window.URL.revokeObjectURL(objUrl);
  } catch (err) { alert("Download failed: " + (err.response?.data?.detail || err.message)); }
};

const EntriesTable = ({ entries, onDelete, testid }) => (
  <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
    <table className="w-full text-sm" data-testid={testid}>
      <thead className="bg-slate-50 border-b border-slate-200 text-[10px] font-bold uppercase text-slate-600">
        <tr>
          <th className="px-3 py-2 text-left">Date</th>
          <th className="px-3 py-2 text-left">Type</th>
          <th className="px-3 py-2 text-left">Item</th>
          <th className="px-3 py-2 text-right">Qty</th>
          <th className="px-3 py-2 text-left">Unit</th>
          <th className="px-3 py-2 text-left">Purpose</th>
          <th className="px-3 py-2 text-left">Issued To</th>
          <th className="px-3 py-2 text-left">Source</th>
          <th className="px-3 py-2 text-left">Ref</th>
          <th className="px-3 py-2 text-left">Notes</th>
          <th className="px-3 py-2 text-right">Action</th>
        </tr>
      </thead>
      <tbody>
        {entries.length === 0 ? (
          <tr><td colSpan={11} className="px-3 py-8 text-center text-slate-400">No stock entries yet.</td></tr>
        ) : entries.map((e) => (
          <tr key={e.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`godown-entry-${e.id}`}>
            <td className="px-3 py-2 font-mono text-xs">{e.date}</td>
            <td className="px-3 py-2">
              {e.entry_type === "out" ? (
                <span className="inline-flex items-center gap-1 text-[10px] font-black px-2 py-0.5 rounded-sm bg-rose-100 text-rose-700"><ArrowUp size={10} /> OUT</span>
              ) : (
                <span className="inline-flex items-center gap-1 text-[10px] font-black px-2 py-0.5 rounded-sm bg-emerald-100 text-emerald-700"><ArrowDown size={10} /> IN</span>
              )}
            </td>
            <td className="px-3 py-2 font-semibold">{e.item_name}</td>
            <td className={`px-3 py-2 text-right font-mono font-bold ${e.entry_type === "out" ? "text-rose-700" : "text-emerald-700"}`}>{e.entry_type === "out" ? "−" : "+"}{(e.qty || 0).toLocaleString()}</td>
            <td className="px-3 py-2 text-slate-500">{e.unit}</td>
            <td className="px-3 py-2">{e.purpose ? <span className={`text-[10px] font-black uppercase px-1.5 py-0.5 rounded-sm ${e.purpose === "manufacturing" ? "bg-amber-100 text-amber-800" : e.purpose === "sales" ? "bg-blue-100 text-blue-800" : "bg-slate-100 text-slate-600"}`}>{e.purpose}</span> : "—"}</td>
            <td className="px-3 py-2 text-slate-700 font-semibold">{e.issued_to || "—"}</td>
            <td className="px-3 py-2"><span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-sm bg-slate-100 text-slate-600">{(e.source || "manual").replace("_", " ")}</span></td>
            <td className="px-3 py-2 font-mono text-xs">{e.ref_no || "—"}</td>
            <td className="px-3 py-2 text-slate-500 max-w-[160px] truncate" title={e.notes}>{e.notes || ""}</td>
            <td className="px-3 py-2 text-right">
              <button onClick={() => onDelete(e.id)} data-testid={`godown-entry-delete-${e.id}`}
                className="text-rose-600 hover:text-rose-800"><Trash size={14} /></button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default function Godown() {
  const [godowns, setGodowns] = useState([]);
  const [form, setForm] = useState({ name: "", location: "" });
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(null);      // godown id
  const [detail, setDetail] = useState(null);          // {godown, entries, totals}
  const [items, setItems] = useState([]);              // item-wise stock in godown
  const [selectedItem, setSelectedItem] = useState(null); // item_name
  const [itemDetail, setItemDetail] = useState(null);  // {entries, totals} for item
  const [entryForm, setEntryForm] = useState({
    entry_type: "in", date: new Date().toISOString().slice(0, 10),
    item_name: "", qty: "", unit: "PCS", notes: "",
    purpose: "manufacturing", issued_to: "",
  });
  const [entrySaving, setEntrySaving] = useState(false);
  const [alerts, setAlerts] = useState(null);

  const fetchGodowns = async () => {
    try { const r = await axios.get(`${API}/godowns`); setGodowns(r.data.items || []); } catch { /* silent */ }
    try { const ra = await axios.get(`${API}/alerts/low-stock`); setAlerts(ra.data); } catch { /* silent */ }
  };
  useEffect(() => { fetchGodowns(); }, []);
  useEscStep(() => {
    if (selectedItem) { setSelectedItem(null); setItemDetail(null); return true; }
    if (selected) { setSelected(null); return true; }
    return false;
  });

  const fetchDetail = async (gid) => {
    try {
      const [r, ri] = await Promise.all([
        axios.get(`${API}/godowns/${gid}/entries`),
        axios.get(`${API}/godowns/${gid}/items`),
      ]);
      setDetail(r.data); setItems(ri.data.items || []);
    } catch (e) { alert(e.response?.data?.detail || "Failed to load entries"); }
  };
  useEffect(() => { if (selected) fetchDetail(selected); else { setDetail(null); setItems([]); setSelectedItem(null); setItemDetail(null); } }, [selected]); // eslint-disable-line

  const fetchItemDetail = async (name) => {
    try { const r = await axios.get(`${API}/godowns/${selected}/entries`, { params: { item: name } }); setItemDetail(r.data); }
    catch (e) { alert(e.response?.data?.detail || "Failed"); }
  };
  useEffect(() => { if (selected && selectedItem) fetchItemDetail(selectedItem); }, [selectedItem]); // eslint-disable-line

  const createGodown = async () => {
    if (!(form.name || "").trim()) { alert("Godown name likhiye"); return; }
    setSaving(true);
    try { await axios.post(`${API}/godowns`, form); setForm({ name: "", location: "" }); fetchGodowns(); }
    catch (e) { alert(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const deleteGodown = async (g) => {
    if (!window.confirm(`Delete godown '${g.name}'?`)) return;
    try { await axios.delete(`${API}/godowns/${g.id}`); if (selected === g.id) setSelected(null); fetchGodowns(); }
    catch (e) { alert(e.response?.data?.detail || "Failed"); }
  };

  const refreshAll = () => { fetchDetail(selected); fetchGodowns(); if (selectedItem) fetchItemDetail(selectedItem); };

  const addEntry = async (fixedItem = null) => {
    if (!selected) return;
    const itemName = fixedItem || entryForm.item_name;
    if (!(itemName || "").trim() || !parseFloat(entryForm.qty)) { alert("Item name aur Qty bhariye"); return; }
    if (entryForm.entry_type === "out" && !(entryForm.issued_to || "").trim()) { alert("Issued To — enter the name of the person taking the goods"); return; }
    setEntrySaving(true);
    try {
      const r = await axios.post(`${API}/godowns/${selected}/entries`, { ...entryForm, item_name: itemName, qty: parseFloat(entryForm.qty) });
      setEntryForm((f) => ({ ...f, item_name: fixedItem ? f.item_name : "", qty: "", notes: "", issued_to: "" }));
      refreshAll();
      if (r.data?.mfg_batch_id) alert("Stock issued ✓ — A lot has been created in the Manufacturing tab (all calculations auto).");
    } catch (e) { alert(e.response?.data?.detail || "Failed"); }
    finally { setEntrySaving(false); }
  };

  const deleteEntry = async (eid) => {
    if (!window.confirm("Delete this stock entry?")) return;
    try { await axios.delete(`${API}/godown-entries/${eid}`); refreshAll(); }
    catch (e) { alert(e.response?.data?.detail || "Failed"); }
  };

  const curItem = items.find((i) => i.item_name === selectedItem);

  return (
    <div className="p-6 space-y-6" data-testid="godown-page">
      <div>
        <h1 className="text-3xl font-black tracking-tight flex items-center gap-2"><Warehouse size={30} /> Godown</h1>
        <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mt-1">
          Multiple godowns · item-wise stock · stock in / out records
        </p>
      </div>

      {/* Low-stock alerts — godown-wise items below their reorder level */}
      {alerts && alerts.count > 0 && (
        <div className="bg-amber-50 border border-amber-300 rounded-sm p-4" data-testid="low-stock-alerts">
          <div className="flex items-center gap-2 mb-2">
            <Warning size={18} weight="fill" className="text-amber-600" />
            <h3 className="text-sm font-black text-amber-900">Low Stock Alerts ({alerts.count})</h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {alerts.godown_alerts.map((a, i) => (
              <div key={`g${i}`} data-testid={`low-stock-godown-${i}`}
                className="bg-white border border-amber-200 rounded-sm px-3 py-1.5 text-xs">
                <b className="text-slate-900">{a.item_name}</b>
                <span className="text-slate-500"> in </span>
                <b className="text-amber-800">{a.godown_name}</b>
                <span className="text-rose-600 font-bold"> — {a.qty} {a.unit}</span>
                <span className="text-slate-400"> (reorder at {a.reorder_level})</span>
              </div>
            ))}
            {alerts.product_alerts.map((a, i) => (
              <div key={`p${i}`} data-testid={`low-stock-product-${i}`}
                className="bg-white border border-amber-200 rounded-sm px-3 py-1.5 text-xs">
                <b className="text-slate-900">{a.item_name}</b>
                <span className="text-slate-500"> overall stock</span>
                <span className="text-rose-600 font-bold"> — {a.current_stock} {a.unit}</span>
                <span className="text-slate-400"> (reorder at {a.reorder_level})</span>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-amber-700 mt-2">Reorder level is set per product in the Products master ("Reorder Level" field).</p>
        </div>
      )}

      {/* Create godown */}
      <div className="bg-white border border-slate-200 rounded-sm p-4">
        <h3 className="text-sm font-black text-slate-900 mb-3">Create New Godown</h3>
        <div className="flex gap-2 flex-wrap items-end">
          <div className="flex-1 min-w-[200px]">
            <Label className="text-xs">Godown Name *</Label>
            <Input value={form.name} data-testid="godown-name-input"
              onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Main Godown" />
          </div>
          <div className="flex-1 min-w-[200px]">
            <Label className="text-xs">Location</Label>
            <Input value={form.location} data-testid="godown-location-input"
              onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="City / address (optional)" />
          </div>
          <Button onClick={createGodown} disabled={saving} data-testid="godown-create-btn"
            className="bg-slate-900 hover:bg-slate-800 text-white font-bold">
            <Plus size={16} /> {saving ? "Adding…" : "Add Godown"}
          </Button>
        </div>
      </div>

      {/* Godown list */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <table className="w-full text-sm" data-testid="godown-list-table">
          <thead className="bg-slate-50 border-b border-slate-200 text-[10px] font-bold uppercase text-slate-600">
            <tr>
              <th className="px-3 py-2 text-left">Godown</th>
              <th className="px-3 py-2 text-left">Location</th>
              <th className="px-3 py-2 text-right">Stock In</th>
              <th className="px-3 py-2 text-right">Stock Out</th>
              <th className="px-3 py-2 text-right">Balance</th>
              <th className="px-3 py-2 text-right">Entries</th>
              <th className="px-3 py-2 text-left">Last Entry</th>
              <th className="px-3 py-2 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {godowns.length === 0 ? (
              <tr><td colSpan={8} className="px-3 py-10 text-center text-slate-400">No godowns yet.</td></tr>
            ) : godowns.map((g) => (
              <tr key={g.id} className={`border-b border-slate-100 hover:bg-slate-50 cursor-pointer ${selected === g.id ? "bg-blue-50/60" : ""}`}
                data-testid={`godown-row-${g.id}`} onClick={() => { setSelectedItem(null); setItemDetail(null); setSelected(selected === g.id ? null : g.id); }}>
                <td className="px-3 py-2 font-black text-slate-900">{selected === g.id ? "▾ " : "▸ "}{g.name}</td>
                <td className="px-3 py-2 text-slate-500">{g.location || "—"}</td>
                <td className="px-3 py-2 text-right font-mono text-emerald-700 font-bold">{(g.qty_in || 0).toLocaleString()}</td>
                <td className="px-3 py-2 text-right font-mono text-rose-700 font-bold">{(g.qty_out || 0).toLocaleString()}</td>
                <td className="px-3 py-2 text-right font-mono font-black">{(g.balance || 0).toLocaleString()}</td>
                <td className="px-3 py-2 text-right text-slate-500">{g.entries || 0}</td>
                <td className="px-3 py-2 text-slate-500">{g.last_date || "—"}</td>
                <td className="px-3 py-2 text-right">
                  <button onClick={(ev) => { ev.stopPropagation(); deleteGodown(g); }} data-testid={`godown-delete-${g.id}`}
                    className="text-rose-600 hover:text-rose-800 text-[11px] font-bold">Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ============ ITEM PAGE (click item → its own page) ============ */}
      {selected && detail && selectedItem && itemDetail && (
        <div className="space-y-4" data-testid="godown-item-page">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <button onClick={() => { setSelectedItem(null); setItemDetail(null); }} data-testid="godown-item-back"
              className="inline-flex items-center gap-1 text-sm font-bold text-slate-600 hover:text-slate-900">
              <CaretLeft size={15} /> {detail.godown.name}
            </button>
            <div className="flex gap-2">
              <button data-testid="godown-item-excel"
                onClick={() => downloadFile(`${API}/godowns/${selected}/stock-export/excel?item=${encodeURIComponent(selectedItem)}`, `${selectedItem.replace(/\s+/g, "_")}_Stock.xlsx`)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-emerald-700 hover:text-white hover:bg-emerald-700 border border-emerald-400 rounded-sm transition-colors">
                <DownloadSimple size={13} /> Excel
              </button>
              <button data-testid="godown-item-pdf"
                onClick={() => downloadFile(`${API}/godowns/${selected}/stock-export/pdf?item=${encodeURIComponent(selectedItem)}`, `${selectedItem.replace(/\s+/g, "_")}_Stock.pdf`)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-rose-700 hover:text-white hover:bg-rose-700 border border-rose-400 rounded-sm transition-colors">
                <DownloadSimple size={13} /> PDF
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-slate-900 rounded-sm p-3">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Item</p>
              <p className="text-lg font-black text-white truncate">{selectedItem}</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-sm p-3">
              <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-700">Total In</p>
              <p className="text-lg font-black text-emerald-700" data-testid="item-total-in">{itemDetail.totals.qty_in.toLocaleString()} {curItem?.unit}</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-sm p-3">
              <p className="text-[10px] font-bold uppercase tracking-widest text-rose-700">Total Out (minus)</p>
              <p className="text-lg font-black text-rose-700" data-testid="item-total-out">−{itemDetail.totals.qty_out.toLocaleString()} {curItem?.unit}</p>
            </div>
            <div className="bg-white border-l-4 border-l-blue-500 border border-slate-200 rounded-sm p-3">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Available Balance</p>
              <p className="text-lg font-black" data-testid="item-balance">{itemDetail.totals.balance.toLocaleString()} {curItem?.unit}</p>
            </div>
          </div>

          {/* Item stock-out entry */}
          <div className="bg-white border border-slate-200 rounded-sm p-4">
            <h3 className="text-sm font-black text-slate-900 mb-3">Stock Entry — {selectedItem}</h3>
            <div className="flex gap-2 flex-wrap items-end">
              <div>
                <Label className="text-xs">Type</Label>
                <select value={entryForm.entry_type} data-testid="item-entry-type"
                  onChange={(e) => setEntryForm({ ...entryForm, entry_type: e.target.value })}
                  className="h-9 px-3 border border-slate-200 rounded-sm text-sm font-semibold block">
                  <option value="out">Stock OUT</option>
                  <option value="in">Stock IN</option>
                </select>
              </div>
              <div>
                <Label className="text-xs">Date</Label>
                <Input type="date" value={entryForm.date} data-testid="item-entry-date"
                  onChange={(e) => setEntryForm({ ...entryForm, date: e.target.value })} className="w-40" />
              </div>
              {entryForm.entry_type === "out" && (
                <>
                  <div>
                    <Label className="text-xs">Purpose / Destination *</Label>
                    <select value={entryForm.purpose} data-testid="item-entry-purpose"
                      onChange={(e) => setEntryForm({ ...entryForm, purpose: e.target.value })}
                      className="h-9 px-3 border border-slate-200 rounded-sm text-sm font-semibold block">
                      <option value="manufacturing">Manufacturing Unit</option>
                      <option value="sales">Sales</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div className="min-w-[160px]">
                    <Label className="text-xs">Issued To / Handed Over To *</Label>
                    <Input value={entryForm.issued_to} data-testid="item-entry-issued-to"
                      onChange={(e) => setEntryForm({ ...entryForm, issued_to: e.target.value })} placeholder="Person / employee name" />
                  </div>
                </>
              )}
              <div>
                <Label className="text-xs">Qty * ({curItem?.unit})</Label>
                <Input type="number" value={entryForm.qty} data-testid="item-entry-qty"
                  onChange={(e) => setEntryForm({ ...entryForm, qty: e.target.value })} className="w-24" />
              </div>
              <div className="flex-1 min-w-[120px]">
                <Label className="text-xs">Notes</Label>
                <Input value={entryForm.notes} data-testid="item-entry-notes"
                  onChange={(e) => setEntryForm({ ...entryForm, notes: e.target.value })} placeholder="optional" />
              </div>
              <Button onClick={() => addEntry(selectedItem)} disabled={entrySaving} data-testid="item-entry-add-btn"
                className="bg-slate-900 hover:bg-slate-800 text-white font-bold">
                {entrySaving ? "Saving…" : "+ Add Entry"}
              </Button>
            </div>
          </div>

          <EntriesTable entries={itemDetail.entries} onDelete={deleteEntry} testid="item-entries-table" />
        </div>
      )}

      {/* ============ GODOWN DETAIL (item list + form + full log) ============ */}
      {selected && detail && !selectedItem && (
        <div className="space-y-4" data-testid="godown-detail">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-white border border-slate-200 rounded-sm p-3">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Godown</p>
              <p className="text-lg font-black">{detail.godown.name}</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-sm p-3">
              <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-700">Total Stock In</p>
              <p className="text-lg font-black text-emerald-700" data-testid="godown-total-in">{detail.totals.qty_in.toLocaleString()}</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-sm p-3">
              <p className="text-[10px] font-bold uppercase tracking-widest text-rose-700">Total Stock Out</p>
              <p className="text-lg font-black text-rose-700" data-testid="godown-total-out">{detail.totals.qty_out.toLocaleString()}</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-sm p-3">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Balance</p>
              <p className="text-lg font-black" data-testid="godown-balance">{detail.totals.balance.toLocaleString()}</p>
            </div>
          </div>

          {/* Item-wise stock — click item → item page */}
          <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
            <div className="px-4 py-2 bg-blue-50 border-b border-blue-200 flex items-center justify-between">
              <p className="text-xs font-black uppercase tracking-widest text-blue-800">Item-wise Stock — click an item to open its page</p>
              <div className="flex gap-2">
                <button data-testid="godown-stock-excel"
                  onClick={() => downloadFile(`${API}/godowns/${selected}/stock-export/excel`, `${detail.godown.name.replace(/\s+/g, "_")}_Stock.xlsx`)}
                  className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-bold text-emerald-700 hover:text-white hover:bg-emerald-700 border border-emerald-400 rounded-sm transition-colors">
                  <DownloadSimple size={10} /> Excel
                </button>
                <button data-testid="godown-stock-pdf"
                  onClick={() => downloadFile(`${API}/godowns/${selected}/stock-export/pdf`, `${detail.godown.name.replace(/\s+/g, "_")}_Stock.pdf`)}
                  className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-bold text-rose-700 hover:text-white hover:bg-rose-700 border border-rose-400 rounded-sm transition-colors">
                  <DownloadSimple size={10} /> PDF
                </button>
              </div>
            </div>
            <table className="w-full text-sm" data-testid="godown-items-table">
              <thead className="bg-slate-50 border-b border-slate-200 text-[10px] font-bold uppercase text-slate-600">
                <tr>
                  <th className="px-3 py-2 text-left">Item</th>
                  <th className="px-3 py-2 text-left">Unit</th>
                  <th className="px-3 py-2 text-right">In</th>
                  <th className="px-3 py-2 text-right">Out</th>
                  <th className="px-3 py-2 text-right">Balance</th>
                  <th className="px-3 py-2 text-right">Entries</th>
                  <th className="px-3 py-2 text-left">Last Entry</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-400 text-xs">No items yet — make a Stock IN entry below.</td></tr>
                ) : items.map((it) => (
                  <tr key={it.item_name} className="border-b border-slate-100 hover:bg-blue-50/50 cursor-pointer"
                    data-testid={`godown-item-row-${it.item_name.replace(/\s+/g, "-")}`}
                    onClick={() => { setEntryForm((f) => ({ ...f, entry_type: "out", unit: it.unit })); setSelectedItem(it.item_name); }}>
                    <td className="px-3 py-2 font-black text-blue-800 underline decoration-dotted underline-offset-4">{it.item_name}</td>
                    <td className="px-3 py-2 text-slate-500">{it.unit}</td>
                    <td className="px-3 py-2 text-right font-mono text-emerald-700 font-bold">{it.qty_in.toLocaleString()}</td>
                    <td className="px-3 py-2 text-right font-mono text-rose-700 font-bold">{it.qty_out.toLocaleString()}</td>
                    <td className="px-3 py-2 text-right font-mono font-black">{it.balance.toLocaleString()}</td>
                    <td className="px-3 py-2 text-right text-slate-500">{it.entries}</td>
                    <td className="px-3 py-2 text-slate-500">{it.last_date || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Manual entry form (godown level) */}
          <div className="bg-white border border-slate-200 rounded-sm p-4">
            <h3 className="text-sm font-black text-slate-900 mb-3">Add Stock In / Stock Out Entry</h3>
            <div className="flex gap-2 flex-wrap items-end">
              <div>
                <Label className="text-xs">Type</Label>
                <select value={entryForm.entry_type} data-testid="godown-entry-type"
                  onChange={(e) => setEntryForm({ ...entryForm, entry_type: e.target.value })}
                  className="h-9 px-3 border border-slate-200 rounded-sm text-sm font-semibold block">
                  <option value="in">Stock IN</option>
                  <option value="out">Stock OUT</option>
                </select>
              </div>
              <div>
                <Label className="text-xs">Date</Label>
                <Input type="date" value={entryForm.date} data-testid="godown-entry-date"
                  onChange={(e) => setEntryForm({ ...entryForm, date: e.target.value })} className="w-40" />
              </div>
              {entryForm.entry_type === "out" && (
                <>
                  <div>
                    <Label className="text-xs">Purpose / Destination *</Label>
                    <select value={entryForm.purpose} data-testid="godown-entry-purpose"
                      onChange={(e) => setEntryForm({ ...entryForm, purpose: e.target.value })}
                      className="h-9 px-3 border border-slate-200 rounded-sm text-sm font-semibold block">
                      <option value="manufacturing">Manufacturing Unit</option>
                      <option value="sales">Sales</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div className="min-w-[160px]">
                    <Label className="text-xs">Issued To / Handed Over To *</Label>
                    <Input value={entryForm.issued_to} data-testid="godown-entry-issued-to"
                      onChange={(e) => setEntryForm({ ...entryForm, issued_to: e.target.value })} placeholder="Person / employee name" />
                  </div>
                </>
              )}
              <div className="flex-1 min-w-[180px]">
                <Label className="text-xs">Item Name *</Label>
                <Input value={entryForm.item_name} data-testid="godown-entry-item"
                  onChange={(e) => setEntryForm({ ...entryForm, item_name: e.target.value })} placeholder="e.g. MS Angle 50x50" />
              </div>
              <div>
                <Label className="text-xs">Qty *</Label>
                <Input type="number" value={entryForm.qty} data-testid="godown-entry-qty"
                  onChange={(e) => setEntryForm({ ...entryForm, qty: e.target.value })} className="w-24" />
              </div>
              <div>
                <Label className="text-xs">Unit</Label>
                <Input value={entryForm.unit} data-testid="godown-entry-unit"
                  onChange={(e) => setEntryForm({ ...entryForm, unit: e.target.value })} className="w-20" />
              </div>
              <div className="flex-1 min-w-[140px]">
                <Label className="text-xs">Notes</Label>
                <Input value={entryForm.notes} data-testid="godown-entry-notes"
                  onChange={(e) => setEntryForm({ ...entryForm, notes: e.target.value })} placeholder="optional" />
              </div>
              <Button onClick={() => addEntry()} disabled={entrySaving} data-testid="godown-entry-add-btn"
                className="bg-slate-900 hover:bg-slate-800 text-white font-bold">
                {entrySaving ? "Saving…" : "+ Add Entry"}
              </Button>
            </div>
          </div>

          <EntriesTable entries={detail.entries} onDelete={deleteEntry} testid="godown-entries-table" />
        </div>
      )}
    </div>
  );
}
